import { useEffect } from 'react'
import {
  useAuth,
  useUser,
  useOrganization,
  useOrganizationList,
} from '@clerk/clerk-react'
import { useOrganizationStore } from '@/stores/organizationStore'

/**
 * Custom hook for checking if the user is authenticated
 * Returns both authentication state and loading state
 */
export function useIsAuthenticated() {
  const { isSignedIn, isLoaded } = useAuth()
  return { isSignedIn, isLoaded }
}

/**
 * Custom hook to get current user with derived properties
 */
export function useAuthUser() {
  const { user, isLoaded } = useUser()

  return {
    user,
    isLoaded,
    fullName: user ? `${user.firstName} ${user.lastName}` : '',
    email: user?.primaryEmailAddress?.emailAddress,
    avatarUrl: user?.imageUrl,
  }
}

/**
 * Custom hook to get user organizations with functionality for organization management
 * Provides data and methods for the TeamSwitcher component
 * Uses the Zustand store for organization state management
 */
export function useAuthOrganizations() {
  const { organization, isLoaded: isOrgLoaded } = useOrganization()
  const {
    userMemberships,
    isLoaded: isMembershipsLoaded,
    setActive,
  } = useOrganizationList({
    userMemberships: true,
  })

  // Zustand store
  const {
    activeOrganization,
    organizationMemberships,
    isLoaded: storeIsLoaded,
    isChangingOrg,
    setActiveOrganization,
    setOrganizationMemberships,
    setIsLoaded,
    setIsChangingOrg,
  } = useOrganizationStore()

  // Sync Clerk state with Zustand store
  // Note: setActiveOrganization is stable from Zustand, safe to omit from deps
  useEffect(() => {
    if (isOrgLoaded && organization !== undefined) {
      // Clone the organization to ensure we don't store proxied objects
      const orgData = organization
        ? {
            id: organization.id,
            name: organization.name || '',
            slug: organization.slug,
            imageUrl: organization.imageUrl,
            // Add any other needed properties here
          }
        : null

      setActiveOrganization(orgData)
    }
     
  }, [isOrgLoaded, organization])

  // Sync memberships with Zustand store
  // Note: setOrganizationMemberships is stable from Zustand, safe to omit from deps
  useEffect(() => {
    if (isMembershipsLoaded && userMemberships?.data !== undefined) {
      // Map to our simplified organization membership format
      const memberships = userMemberships.data.map((membership) => ({
        id: membership.id,
        role: membership.role,
        organization: {
          id: membership.organization.id,
          name: membership.organization.name || '',
          slug: membership.organization.slug,
          imageUrl: membership.organization.imageUrl,
          // Add any other needed properties here
        },
      }))

      setOrganizationMemberships(memberships)
    }
     
  }, [isMembershipsLoaded, userMemberships?.data])

  // Update loading state
  // Note: setIsLoaded is stable from Zustand, safe to omit from deps
  useEffect(() => {
    setIsLoaded(isOrgLoaded && isMembershipsLoaded)
     
  }, [isOrgLoaded, isMembershipsLoaded])

  // Function to refresh organizations by triggering a re-render
  const refreshOrganizations = async () => {
    try {
      // Set loading state to trigger UI updates
      setIsChangingOrg(true)

      // Small delay to ensure any API changes have propagated
      await new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, 300)
        // Store timeout ID for potential cleanup
        return () => clearTimeout(timeoutId)
      })

      // Force a state change to trigger rerenders and refreshes
      setIsChangingOrg(false)
      return true
    } catch {
      setIsChangingOrg(false)
      return false
    }
  }

  // Function to switch to another organization
  const switchOrganization = async (organizationId: string) => {
    if (!setActive) return false

    try {
      setIsChangingOrg(true)
      await setActive({ organization: organizationId })

      // Small delay before turning off loading state
      // This gives time for Clerk to update the org context
      await new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, 300)
        return () => clearTimeout(timeoutId)
      })

      setIsChangingOrg(false)
      return true
    } catch {
      // Use more elegant error handling instead of console
      setIsChangingOrg(false)
      return false
    }
  }

  // Function to update organization details
  const updateOrganization = async (data: { name: string; slug?: string }) => {
    if (!organization) return false

    try {
      setIsChangingOrg(true)
      await organization.update(data)

      // Update the store after successful API update
      if (activeOrganization) {
        setActiveOrganization({
          ...activeOrganization,
          name: data.name,
          slug: data.slug ?? activeOrganization.slug,
        })
      }

      // Small delay to ensure changes are reflected in the UI
      await new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, 300)
        return () => clearTimeout(timeoutId)
      })

      setIsChangingOrg(false)
      return true
    } catch {
      setIsChangingOrg(false)
      return false
    }
  }

  // Function to invite someone to the organization
  const inviteMember = async (email: string, role: string) => {
    if (!organization) return false

    try {
      setIsChangingOrg(true)
      await organization.inviteMember({ emailAddress: email, role })

      // Small delay to ensure changes are reflected in the UI
      await new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, 300)
        return () => clearTimeout(timeoutId)
      })

      setIsChangingOrg(false)
      return true
    } catch {
      setIsChangingOrg(false)
      return false
    }
  }

  // Function to delete the organization
  const deleteOrganization = async () => {
    if (!organization) return false

    try {
      setIsChangingOrg(true)
      await organization.destroy()

      // Reset organization in the store
      setActiveOrganization(null)

      // Small delay to ensure changes are reflected in the UI
      await new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, 300)
        return () => clearTimeout(timeoutId)
      })

      setIsChangingOrg(false)
      return true
    } catch {
      setIsChangingOrg(false)
      return false
    }
  }

  return {
    // Current organization
    organization: activeOrganization,
    // All user memberships
    organizations: organizationMemberships,
    // Loading state
    isLoaded: storeIsLoaded,
    isChangingOrg,
    // Organization management functions
    switchOrganization,
    updateOrganization,
    inviteMember,
    deleteOrganization,
    refreshOrganizations,
    // For backward compatibility
    currentOrg: activeOrganization,
  }
}
